import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames'
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { Button, Shelf, Spinner } from '../../../components/widgets';
import AutoCompleteField from '../../../components/molecules/AutoCompleteField';
import CreativeResultCard from './CreativeResultCard';
import { testAIDimension } from '../../services';
import { buildMixpanelPayload, MIXPANEL, PLAYGROUND_TRANSLATIONS } from '../../consts';
import { trackMixpanelEvent } from '../../../utils/general';
import toaster from '../../../utils/toaster';
import css from './DimensionPlaygroundPopup.css';

const DimensionPlaygroundPopup = ({ open, onClose, dimensionConfig, translate }) => {
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [creatives, setCreatives] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [summary, setSummary] = useState({ total: 0, succeeded: 0, failed: 0 });
    const [selectedSource, setSelectedSource] = useState(null);
    const [availableSources, setAvailableSources] = useState([]);

    const containerRef = useRef(null);
    const subtitleRef = useRef(null);
    const summaryRef = useRef(null);

    useEffect(() => {
        if (open && containerRef.current) {
            const updateHeights = () => {
                const subtitleHeight = subtitleRef.current?.offsetHeight || 0;
                const summaryHeight = summaryRef.current?.offsetHeight || 0;
                containerRef.current.style.setProperty('--subtitle-height', `${subtitleHeight}px`);
                containerRef.current.style.setProperty('--summary-bar-height', `${summaryHeight}px`);
            };
            // Small delay to ensure DOM is rendered
            requestAnimationFrame(updateHeights);
        }
    }, [open, dimensionConfig?.description, summary.total]);

    const fetchResults = useCallback(
        async (offset = 0, isLoadMore = false, sourceFilter = null) => {
            if (!dimensionConfig) return;

            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
                setError(null);
                setCreatives([]);
                setSummary({ total: 0, succeeded: 0, failed: 0 });
            }

            try {
                const response = await testAIDimension({
                    display_name: dimensionConfig.display_name,
                    description: dimensionConfig.description,
                    options: dimensionConfig.options,
                    value_type: dimensionConfig.value_type,
                    app_ids: dimensionConfig.apps,
                    offset,
                    source: sourceFilter,
                });

                if (isLoadMore) {
                    setCreatives(prev => [...prev, ...response.creatives]);
                    setSummary(prev => ({
                        total: prev.total + response.summary.total,
                        succeeded: prev.succeeded + response.summary.succeeded,
                        failed: prev.failed + response.summary.failed,
                    }));
                } else {
                    setCreatives(response.creatives);
                    setSummary(response.summary);
                }
                setHasMore(response.has_more);

                // Update available sources (only on initial fetch, not load more)
                if (!isLoadMore && response.available_sources) {
                    setAvailableSources(response.available_sources);
                }
            } catch (err) {
                if (isLoadMore) {
                    toaster.error(translate(PLAYGROUND_TRANSLATIONS.POPUP_ERROR));
                } else {
                    setError(err?.message || 'Failed to test dimension');
                }
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [dimensionConfig]
    );

    useEffect(() => {
        if (open) {
            fetchResults(0, false, selectedSource);
        } else {
            setCreatives([]);
            setError(null);
            setHasMore(false);
            setSummary({ total: 0, succeeded: 0, failed: 0 });
            setSelectedSource(null);
            setAvailableSources([]);
        }
    }, [open, fetchResults, selectedSource]);

    useEffect(() => {
        if (!open) {
            return () => {};
        }

        const handleKeyDown = e => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [open]);

    const handleRetry = () => {
        fetchResults(0, false, selectedSource);
    };

    const handleLoadMore = () => {
        trackMixpanelEvent(
            MIXPANEL.EVENT_PREFIX,
            MIXPANEL.ACTIONS.LOAD_MORE_CREATIVES,
            buildMixpanelPayload(dimensionConfig, [])
        );
        fetchResults(creatives.length, true, selectedSource);
    };

    const handleSourceFilterChange = value => {
        const source = value?.value || null;
        trackMixpanelEvent(MIXPANEL.EVENT_PREFIX, MIXPANEL.ACTIONS.FILTER_SOURCE, {
            ...buildMixpanelPayload(dimensionConfig, []),
            source: source || 'all',
        });
        setSelectedSource(source);
    };

    const sourceOptions = useMemo(
        () =>
            availableSources.map(source => ({
                value: source.name,
                label: source.name,
                icon: source.icon ? <img className={css.sourceIcon} src={source.icon} alt={source.name} /> : null,
            })),
        [availableSources]
    );

    const renderContent = () => {
        if (loading) {
            return (
                <div className={css.loadingContainer}>
                    <Spinner show />
                    <span className={css.loadingText}>{translate(PLAYGROUND_TRANSLATIONS.POPUP_LOADING)}</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className={css.errorContainer}>
                    <span className={css.errorText}>{translate(PLAYGROUND_TRANSLATIONS.POPUP_ERROR)}</span>
                    <Button
                        text={translate(PLAYGROUND_TRANSLATIONS.POPUP_RETRY)}
                        type="secondary"
                        onClick={handleRetry}
                    />
                </div>
            );
        }

        if (!creatives || creatives.length === 0) {
            return (
                <div className={css.emptyContainer}>
                    <span className={css.emptyText}>{translate(PLAYGROUND_TRANSLATIONS.NO_CREATIVES)}</span>
                </div>
            );
        }

        return (
            <>
                <div className={css.creativesGrid}>
                    {creatives.map(creative => (
                        <CreativeResultCard key={creative.creative_hash} creative={creative} />
                    ))}
                </div>
                {hasMore && (
                    <div className={css.loadMoreContainer}>
                        <Button
                            text={translate(PLAYGROUND_TRANSLATIONS.LOAD_MORE)}
                            type="secondary"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            showSpinner={loadingMore}
                            Icon={loadingMore ? '' : 'arrow-down'}
                        />
                    </div>
                )}
            </>
        );
    };

    const isSummaryBar = !loading || availableSources.length > 0;

    return (
        <Shelf
            open={open}
            onClose={onClose}
            headerText={dimensionConfig.display_name}
            shelfSize="large"
            headerIconName="sparkles"
            closeOnClickOutside={false}
        >
            <div className={css.container} ref={containerRef}>
                {dimensionConfig?.description && (
                    <span
                        className={classNames(css.subtitle, { [css.subtitleDivider]: !isSummaryBar })}
                        ref={subtitleRef}
                        title={dimensionConfig.description}
                    >
                        <b>Description: </b>"{dimensionConfig.description}"
                    </span>
                )}
                {isSummaryBar && (
                    <div className={css.summaryBar} ref={summaryRef}>
                        {availableSources.length > 0 && (
                            <div className={css.sourceFilter}>
                                <AutoCompleteField
                                    label={PLAYGROUND_TRANSLATIONS.SOURCE_FILTER_PLACEHOLDER}
                                    options={sourceOptions}
                                    value={
                                        selectedSource ? [sourceOptions.find(opt => opt.value === selectedSource)] : []
                                    }
                                    onChange={handleSourceFilterChange}
                                    isMulti={false}
                                    clearable
                                    advanced
                                    disabled={loading}
                                />
                            </div>
                        )}
                        {!loading && summary.total > 0 && (
                            <span className={css.summary}>
                                <span>{summary.succeeded}</span>/{summary.total} processed successfully
                                {summary.failed > 0 && (
                                    <>
                                        {' '}
                                        (<span>{summary.failed}</span> failed)
                                    </>
                                )}
                            </span>
                        )}
                    </div>
                )}
                <div className={css.content}>{renderContent()}</div>
            </div>
        </Shelf>
    );
};

DimensionPlaygroundPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    dimensionConfig: PropTypes.shape({
        display_name: PropTypes.string,
        description: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
        value_type: PropTypes.number,
        apps: PropTypes.arrayOf(PropTypes.number),
    }).isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(DimensionPlaygroundPopup);
